import Router from "next/router";
import { normalizeDashboardPathname } from "@/hooks/useDashboardRouter";
import { buildSha } from "@/lib/buildInfo";

const SESSIONS_ROUTE = "/[org]/sessions/[[...sessionPath]]";
// Crawlers name themselves with a "bot/", "bot-", "bot;" or "bot)" token or a
// "+http" contact URL; a bare "bot" suffix would also match devices like
// "CUBOT NOTE 20 PRO".
// Mirrored in FrontendMetricsController::BOT_USER_AGENT.
const BOT_USER_AGENT =
  /Headless|bot[-/;)]|\+https?:\/\/|crawl|spider|slurp|Lighthouse|PhantomJS|Puppeteer|Playwright/i;

export function isBotWebVitalsClient(): boolean {
  return (
    typeof navigator !== "undefined" &&
    (navigator.webdriver === true || BOT_USER_AGENT.test(navigator.userAgent))
  );
}

/**
 * The new-session page (`/{org}/sessions/new`) shares the sessions catch-all
 * route pattern with session threads; report it under its own label so the
 * two chart separately in Grafana. The session segment comes from `asPath`
 * (`/acme/sessions/new` → `new`) rather than `Router.query`, which is still
 * empty when TTFB and LCP fire on a statically optimized first load.
 *
 * The `next/router` singleton has no instance under the App Router (its
 * getters throw), so the route then comes from `window.location`.
 */
export function reportedRoute(): string | null {
  const router = Router.router;
  const pathname =
    router?.pathname ??
    (typeof window !== "undefined"
      ? normalizeDashboardPathname(window.location.pathname)
      : null);
  if (!pathname) return null;
  if (pathname !== SESSIONS_ROUTE) return pathname;
  const path = (router?.asPath ?? window.location.pathname).split(/[?#]/)[0];
  const sessionSegment = path.split("/").filter(Boolean)[2];
  return sessionSegment === "new" ? "/[org]/sessions/new" : SESSIONS_ROUTE;
}

function buildShaField(): Record<string, string> {
  const sha = buildSha();
  return sha && /^[0-9a-f]{7,40}$/i.test(sha) ? { build_sha: sha } : {};
}

/**
 * Sent via `fetch` with `keepalive` and a form-encoded body: the
 * CORS-safelisted content type needs no preflight, which browsers often
 * cancel during unload, exactly when INP and CLS fire. The endpoint also
 * takes signed-out posts, so it accepts only web vitals and navigation timings.
 */
export function postFrontendMetric(fields: Record<string, string>): void {
  void fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/frontend/page-metrics`, {
    method: "POST",
    keepalive: true,
    credentials: "include",
    body: new URLSearchParams({ ...buildShaField(), ...fields }),
  }).catch(() => {});
}

/**
 * Kept apart from `useWebVitalsReporting` so components can report a vital
 * without importing `next/web-vitals`, whose bundle reads `__dirname` and
 * breaks under Vite (Playwright component tests).
 */
export function reportWebVital(
  name: string,
  value: number,
  route: string,
  breakdown: {
    web_vital_element?: string;
    web_vital_proof_source?: string;
  } = {},
): void {
  if (isBotWebVitalsClient()) return;

  postFrontendMetric({
    web_vital: name,
    web_vital_value: String(value),
    web_vital_route: route,
    ...breakdown,
  });
}
